(function ($, window, document) {
    'use strict';

    /**
     * Configuration object (CFG) for managing application settings and localization.
     * This object contains default values and integrates optional overrides from the `WayGenAI` global object.
     *
     * @property {string} baseUrl - The base URL for API requests. Defaults to '/user' if not set by `WayGenAI.apiBase`.
     * @property {string} redirect - The default redirection URL. Defaults to '/user/' if not overridden by `WayGenAI.redirect`.
     * @property {string} redirect_plan - The redirection URL for the plan usage page. Defaults to '/user/plan-usage/' if not overridden by `WayGenAI.redirectPlan`.
     * @property {Object} i18n - Internationalization and localization messages. Contains default error/success messages with the option to override using `WayGenAI.i18n`.
     */
    const CFG = {
        baseUrl: window.WayGenAI?.apiBase || '/user',
        redirect: window.WayGenAI?.redirect || '/user/',
        redirect_plan: window.WayGenAI?.redirectPlan || '/user/plan-usage/',
        i18n: Object.assign({
            invalidEmail: 'Please enter a valid email address.',
            pwWeak: 'Password does not meet the strength requirements.',
            pwMismatch: 'Passwords do not match.',
            firstNameBad: 'First name contains invalid characters.',
            lastNameBad: 'Last name contains invalid characters.',
            mustBe16: 'You must confirm you are 16+.',
            mustAccept: 'You must accept the Privacy Policy and Terms & Conditions.',
            emailUnavailable: 'Registration could not be completed. Please try a different email or sign in.',
            disposableBad: 'Disposable/temporary emails are not allowed.',
            fallback: 'Something went wrong. Please try again.',
            successTitle: 'Almost done — check your email',
            successBody: 'We sent a verification link to your email. It can take a minute and may land in spam. Meanwhile, your account is ready.',
            strong: 'strong',
            weak: 'weak'
        }, window.WayGenAI?.i18n || {})
    };

    /**
     * Redirects to a safe URL based on the provided input. This method ensures that the URL is on the same origin,
     * uses a valid HTTP/HTTPS protocol, and optionally retains the query parameters.
     *
     * @param {string} url - The target URL to redirect to. This can be either a relative or an absolute URL.
     * @param {Object} [options] - Additional options to customize the redirection behavior.
     * @param {string} [options.fallback='/user/'] - The fallback URL to use if the provided URL is invalid or unsafe.
     * @param {boolean} [options.keepQuery=false] - A flag indicating whether to retain query parameters in the URL.
     * @return {string} The normalized and safe URL to which the redirection can occur.
     */
    function safeRedirect(url, {fallback = '/user/', keepQuery = false} = {}) {
        try {
            const base = window.location.origin;

            // treat url as relative against current origin
            const u = new URL(typeof url === 'string' ? url : '', base);

            // block weird schemes and scheme-relative
            if (u.protocol !== 'https:' && u.protocol !== 'http:') return fallback;

            // only same-origin allowed
            if (u.origin !== base) return fallback;

            // only paths under this site; normalize to path (optionally keep query)
            const path = u.pathname.startsWith('/') ? u.pathname : '/' + u.pathname;
            const normalized = path.endsWith('/') ? path : path + '/';
            return normalized + (keepQuery ? u.search : '');
        } catch {
            return fallback;
        }
    }

    /**
     * Clears the cached user information used by the site header.
     *
     * @return {void} This method does not return a value.
     */
    function clearCachedUser() {
        try {
            localStorage.removeItem('wg_user');
        } catch (_) {
        }
    }

    /**
     * Clears all error messages and resets input fields to their default state.
     * This method hides any global error messages, clears field-specific error texts,
     * and removes invalid input field styling.
     *
     * @return {void} Does not return a value.
     */
    function clearErrors() {
        $('#reg-global-error').hide().text('');
        $('.field-error').text('');
        $('.input').removeClass('is-invalid');
    }

    /**
     * Displays a validation error message for a specific field and applies an invalid style.
     *
     * @param {string} field - The ID of the field for which the error message should be shown.
     * @param {string} msg - The error message to display.
     * @return {void} This function does not return a value.
     */
    function showFieldError(field, msg) {
        const $err = $(`[data-error-for="${field}"]`);
        if ($err.length) $err.text(msg);
        const $field = $('#' + field);
        if ($field.length) $field.addClass('is-invalid');
    }

    /**
     * Displays a global error message in the designated error container.
     *
     * @param {string} msg - The message to be displayed in the global error container.
     * @return {void} This method does not return a value.
     */
    function showGlobal(msg) {
        $('#reg-global-error').text(msg).show();
    }

    /**
     * Toggles the loading state for a button element by enabling or disabling it
     * and showing or hiding a spinner within the button.
     *
     * @param {jQuery} $btn The button element to apply the loading state to, wrapped as a jQuery object.
     * @param {boolean} on A boolean value where `true` enables the loading state (disables the button and shows the spinner),
     *                     and `false` disables the loading state (enables the button and hides the spinner).
     * @return {void}
     */
    function setLoading($btn, on) {
        $btn.prop('disabled', on);
        $btn.find('.spinner').toggle(on);
    }

    /**
     * Validates whether the given input is a valid email address.
     *
     * @param {string} v - The string value to be validated as an email address.
     * @return {boolean} Returns true if the input is a valid email address, otherwise false.
     */
    function isEmail(v) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    }

    /**
     * Validates if a given name string meets the specified criteria.
     * The name must contain only letters, numbers, spaces, hyphens, and apostrophes.
     *
     * @param {string} v - The name string to validate.
     * @return {boolean} Returns true if the name is valid or if the input is empty, otherwise false.
     */
    function isValidName(v) {
        if (!v) return true;
        return /^[\w\s\-']+$/u.test(v.trim());
    }

    /**
     * Checks if a given password is strong based on specific criteria.
     * A password is considered strong if it:
     * - Is at least 8 characters long
     * - Contains at least one lowercase letter
     * - Contains at least one uppercase letter
     * - Contains at least one numerical digit
     * - Contains at least one special character
     *
     * @param {string} pw - The password to evaluate.
     * @return {boolean} Returns true if the password is strong, otherwise false.
     */
    function isStrongPassword(pw) {
        if (!pw || pw.length < 8) return false;
        if (!/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};'\:"|,<.>\/\?`~]+$/.test(pw)) return false;
        if (!/[a-z]/.test(pw)) return false;
        if (!/[A-Z]/.test(pw)) return false;
        if (!/[0-9]/.test(pw)) return false;
        if (!/[^a-zA-Z0-9]/.test(pw)) return false;
        return true;
    }

    /**
     * Stores a user object as a JSON string in the local storage under the key 'wg_user'.
     * If an error occurs during the storage process, it is silently caught and ignored.
     *
     * @param {Object} user - The user object to be stored in local storage.
     * @return {void}
     */
    function setUser(user) {
        try {
            localStorage.setItem('wg_user', JSON.stringify(user));
        } catch (_) {
        }
    }

    // toggle password visibility (works for any .pw-toggle next to an <input>)
    $(document).on('click', '.pw-toggle', function () {
        const $btn = $(this);
        const $input = $btn.siblings('input');
        if (!$input.length) return;
        const isHidden = $input.attr('type') === 'password';
        $input.attr('type', isHidden ? 'text' : 'password');
        const $icon = $btn.find('i');
        $icon.toggleClass('fa-eye', isHidden).toggleClass('fa-eye-slash', !isHidden);
    });

    // strength meter for main password
    $('#reg-password').on('input', function () {
        const pw = $(this).val();
        const strong = isStrongPassword(pw);
        const $m = $('#reg-meter');
        $m.removeClass('ok weak');
        if (!pw) {
            $m.text('');
            return;
        }
        if (strong) $m.addClass('ok').text(CFG.i18n.strong); else $m.addClass('weak').text(CFG.i18n.weak);
    });

    // clear field/global errors on input/change
    $(document).on('input', '#reg-email, #reg-first, #reg-last, #reg-password, #reg-password2', function () {
        const id = this.id;
        $(`[data-error-for="${id}"]`).text('');
        $(this).removeClass('is-invalid');
        $('#reg-global-error').hide().text('');
    });
    $(document).on('change', '#consent-age, #consent-terms, #consent-news', function () {
        const id = this.id;
        $(`[data-error-for="${id}"]`).text('');
        $('#reg-global-error').hide().text('');
    });

    /**
     * Verifies user authentication status on page load by making an AJAX request to retrieve user information.
     * Clears the token before initiating the request and updates user state upon success.
     * Redirects the user based on the URL query parameters or to a default fallback if no specific redirect is required.
     * If the request fails, clears the token and focuses on the registration email input field.
     *
     * @return {void} This method does not return a value.
     */
    (function verifyAuthOnLoad() {
        clearCachedUser();
        $.ajax({
            url: CFG.baseUrl + '/me',
            method: 'GET',
            dataType: 'json'
        })
            .done(function (res) {
                setUser(res.user || res)
                const params = new URLSearchParams(window.location.search);
                if (params.has('plan')) {
                    window.location.assign(safeRedirect(CFG.redirect_plan, {fallback: '/user/plan-usage/'}));
                } else {
                    window.location.assign(safeRedirect(CFG.redirect, {fallback: '/user/'}));
                }
            })
            .fail(function () {
                clearCachedUser();
                $('#reg-email').trigger('focus');
            });
    })();

    // submit register
    $(document).on('submit', '#wg-register-form', function (e) {
        e.preventDefault();
        clearErrors();
        const $btn = $('#reg-submit');
        setLoading($btn, true);

        const email = $('#reg-email').val().trim();
        const first = $('#reg-first').val().trim();
        const last = $('#reg-last').val().trim();
        const pw1 = $('#reg-password').val();
        const pw2 = $('#reg-password2').val();
        const over16 = $('#consent-age').is(':checked');
        const accept = $('#consent-terms').is(':checked');
        const news = $('#consent-news').is(':checked');

        let hasError = false;
        const firstInvalid = [];

        if (!isEmail(email)) {
            showFieldError('reg-email', CFG.i18n.invalidEmail);
            hasError = true;
            firstInvalid.push('#reg-email');
        }
        if (!isValidName(first)) {
            showFieldError('reg-first', CFG.i18n.firstNameBad);
            hasError = true;
            firstInvalid.push('#reg-first');
        }
        if (!isValidName(last)) {
            showFieldError('reg-last', CFG.i18n.lastNameBad);
            hasError = true;
            firstInvalid.push('#reg-last');
        }
        if (!isStrongPassword(pw1)) {
            showFieldError('reg-password', CFG.i18n.pwWeak);
            hasError = true;
            firstInvalid.push('#reg-password');
        }
        if (pw1 !== pw2) {
            showFieldError('reg-password2', CFG.i18n.pwMismatch);
            hasError = true;
            firstInvalid.push('#reg-password2');
        }
        if (!over16) {
            showFieldError('consent-age', CFG.i18n.mustBe16);
            hasError = true;
            firstInvalid.push('#consent-age');
        }
        if (!accept) {
            showFieldError('consent-terms', CFG.i18n.mustAccept);
            hasError = true;
            firstInvalid.push('#consent-terms');
        }

        // mark inputs invalid (visual)
        firstInvalid.forEach(sel => {
            if (sel.startsWith('#reg-')) $(sel).addClass('is-invalid');
        });

        if (hasError) {
            // scroll to the first invalid field
            const sel = firstInvalid[0];
            if (sel) {
                try {
                    document.querySelector(sel)?.scrollIntoView({behavior: 'smooth', block: 'center'});
                } catch (_) {
                }
            }
            setLoading($btn, false);
            return;
        }

        const payload = {
            email: email,
            password: pw1,
            first_name: first || '',
            last_name: last || '',
            news_subscribe: !!news,
        };

        $.ajax({
            url: CFG.baseUrl + '/register',
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            data: JSON.stringify(payload)
        })
            .done(function (res) {
                setUser(res.user || res);

                // show success panel and message
                $('#wg-register-form').hide();
                $('#reg-success').hide().text('');

                const $panel = $('#reg-success-panel').show();
                $panel.find('.auth-subtitle').text(CFG.i18n.successBody)
                    .prev('.auth-title').text(CFG.i18n.successTitle);

                const $block = $panel.closest('.auth-card');
                $block.find('>.auth-title, >.auth-subtitle, >.auth-footer').hide();
            })
            .fail(function (jqXHR) {
                const status = jqXHR.status;
                const resp = jqXHR.responseJSON;

                if (status === 400) {
                    const d = (resp?.detail || '').toLowerCase();
                    if (d.includes('disposable')) {
                        showFieldError('reg-email', CFG.i18n.disposableBad);
                        $('#reg-email').addClass('is-invalid');
                        setLoading($btn, false);
                        return;
                    }
                    if (d.includes('registration could not')) {
                        showFieldError('reg-email', CFG.i18n.emailUnavailable);
                        $('#reg-email').addClass('is-invalid');
                        setLoading($btn, false);
                        return;
                    }
                    showGlobal(resp?.detail || CFG.i18n.fallback);
                    setLoading($btn, false);
                    return;
                }

                if (status === 422 && Array.isArray(resp?.detail)) {
                    resp.detail.forEach(function (err) {
                        const loc = (err.loc || []).slice(-1)[0];
                        const msg = err.msg || CFG.i18n.fallback;
                        if (loc === 'email') {
                            showFieldError('reg-email', msg);
                            $('#reg-email').addClass('is-invalid');
                        } else if (loc === 'password' || loc === 'new_password') {
                            showFieldError('reg-password', msg);
                            $('#reg-password').addClass('is-invalid');
                        } else if (loc === 'first_name') {
                            showFieldError('reg-first', msg);
                            $('#reg-first').addClass('is-invalid');
                        } else if (loc === 'last_name') {
                            showFieldError('reg-last', msg);
                            $('#reg-last').addClass('is-invalid');
                        } else {
                            showGlobal(msg);
                        }
                    });
                    setLoading($btn, false);
                    return;
                }

                if (status === 0) {
                    showGlobal(CFG.i18n.fallback);
                } else {
                    showGlobal(resp?.detail || CFG.i18n.fallback);
                }
                setLoading($btn, false);
            });
    });

})(jQuery, window, document);
